import { ItemInstance } from '@headless-tree/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, IconFormIcons, Plus } from 'shared/assets/icons';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { ListSectionLifeAreaHeader } from 'shared/components/ui/section-header-life-area';
import { InternalTreeItem } from 'shared/components/ui/virtualized-tree-list';
import { DataType } from 'shared/types/data-type';
import { LifeArea } from 'shared/types/life-area';

import * as Styled from './tree-section-header.style';

// Section header for tree lists: life-area sections plus the Completed,
// Archived and deadline-quarter sections (via overrideTitle).
export type TreeSectionHeaderProps = {
  item: ItemInstance<InternalTreeItem>;
  lifeArea: LifeArea | undefined;
  denseFormat?: boolean;
  overrideTitle?: string;
  overrideIcon?: React.FC<React.SVGProps<SVGSVGElement>> | null;
  disableLink?: boolean;
  useSecondaryTitle?: boolean;
  isFirst?: boolean;
  onAddGoal?: () => void;
};

export const TreeSectionHeader: React.FC<TreeSectionHeaderProps> = ({
  item,
  lifeArea,
  denseFormat,
  overrideTitle,
  overrideIcon,
  disableLink,
  useSecondaryTitle,
  isFirst,
  onAddGoal,
}) => {
  const { t } = useTranslation();

  const toggleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    item.isExpanded() ? item.collapse() : item.expand();
  };

  const handleAddGoal = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddGoal?.();
  };

  const title = // Fix usage of ?? operator: parens or correct structure
    overrideTitle ??
    lifeArea?.name ??
    t('general.sections.life-areas.other.title');

  const icon =
    overrideIcon === undefined
      ? lifeArea
        ? IconFormIcons[lifeArea.iconName]
        : null
      : overrideIcon;

  return (
    <Styled.HeaderContainer
      $denseFormat={denseFormat}
      $isFirst={isFirst}
      data-component-type={DataType.LifeArea}
      data-id={item.getItemData().sourceId}
      data-title={title}
    >
      <ListSectionLifeAreaHeader
        id={disableLink ? undefined : item.getItemData().sourceId}
        name={title}
        icon={icon ?? undefined}
        color={lifeArea?.color ?? undefined}
        isFrozen={!!lifeArea?.frozenAt}
        image={lifeArea?.visionPreview?.image}
        isSecondaryTitle={useSecondaryTitle}
      />
      <Styled.ActionsContainer>
        <Styled.HoverActions>
          {onAddGoal && (
            <IconButton
              icon={Plus}
              onClick={handleAddGoal}
              color={Colors.Default}
            />
          )}
        </Styled.HoverActions>
        <IconButton
          icon={ChevronLeft}
          onClick={toggleOpen}
          rotation={item.isExpanded() ? -90 : 0}
          color={Colors.Default}
        />
      </Styled.ActionsContainer>
    </Styled.HeaderContainer>
  );
};
